// src/components/mitchRoom/revenueAnalytics/BreakdownCharts.js
import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { formatCurrency, formatPercent } from 'utils/format';

const SOURCE_COLORS = {
  stripe: '#6772e5',
  woocommerce: '#96588a',
  unknown: '#9e9e9e',
};

// Aligned with the RevenueChart series palette so the same order type reads
// as the same colour across the Overview tab.
const TYPE_COLORS = {
  new_subscription: '#59A14F',
  resubscription: '#76B7B2',
  renewal: '#F28E2B',
  one_time: '#B07AA1',
  other: '#9C755F',
};

const TYPE_LABELS = {
  new_subscription: 'New Subscription',
  resubscription: 'Resubscription',
  renewal: 'Renewal',
  one_time: 'One Time',
};

const gbpWhole = (value) => formatCurrency(value, { whole: true });

const renderCustomLabel = ({ percentage }) => {
  if (percentage < 5) return null;
  return `${percentage.toFixed(0)}%`;
};

function ChartCard({ title, children }) {
  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      <h3 className="mb-2 text-token-xl font-semibold">{title}</h3>
      {children}
    </div>
  );
}

function EmptyMessage({ children }) {
  return (
    <div className="flex h-[200px] items-center justify-center">
      <p className="text-token-md text-muted-foreground">{children}</p>
    </div>
  );
}

export default function BreakdownCharts({ breakdown, timeSeries }) {
  // Calculate by_type from time series data (more accurate and consistent)
  const byType = useMemo(() => {
    if (!timeSeries || timeSeries.length === 0) {
      // Fallback to breakdown data if no time series
      return (breakdown?.by_type || []).map((item) => ({
        ...item,
        name: TYPE_LABELS[item.category] || item.category.replace('_', ' '),
        value: item.revenue,
        color: TYPE_COLORS[item.category] || TYPE_COLORS.other,
      }));
    }

    // Sum up revenue by type from time series
    const totals = {
      new_subscription: 0,
      resubscription: 0,
      renewal: 0,
      one_time: 0,
    };

    timeSeries.forEach((point) => {
      totals.new_subscription += point.new_subscription || 0;
      totals.resubscription += point.resubscription || 0;
      totals.renewal += point.renewal || 0;
      totals.one_time += point.one_time || 0;
    });

    const total = totals.new_subscription + totals.resubscription + totals.renewal + totals.one_time;

    return Object.entries(totals)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        category: key,
        name: TYPE_LABELS[key] || key,
        value: value,
        revenue: value,
        percentage: total > 0 ? (value / total) * 100 : 0,
        color: TYPE_COLORS[key] || TYPE_COLORS.other,
      }));
  }, [timeSeries, breakdown]);

  const bySource = useMemo(() => {
    return (breakdown?.by_source || []).map((item) => ({
      ...item,
      name: item.category,
      value: item.revenue,
      color: SOURCE_COLORS[item.category] || SOURCE_COLORS.unknown,
    }));
  }, [breakdown]);

  const typeTotal = useMemo(
    () => byType.reduce((sum, item) => sum + (item.value || 0), 0),
    [byType]
  );

  // Sources that actually earned anything in the selected range — once
  // WooCommerce is fully retired a single-slice pie is wasted space, so we
  // collapse to a one-line note instead.
  const activeSources = useMemo(() => bySource.filter((s) => (s.value || 0) > 0), [bySource]);

  const hasNoData = !breakdown && (!timeSeries || timeSeries.length === 0);

  if (hasNoData) {
    return (
      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Revenue by Source">
          <EmptyMessage>No data available</EmptyMessage>
        </ChartCard>
        <ChartCard title="Revenue by Order Type">
          <EmptyMessage>No data available</EmptyMessage>
        </ChartCard>
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
      {/* By Source */}
      <ChartCard title="Revenue by Source">
        {activeSources.length === 0 ? (
          <EmptyMessage>No source data</EmptyMessage>
        ) : activeSources.length === 1 ? (
          <div className="flex h-[280px] flex-col items-center justify-center gap-1">
            <span
              className="inline-flex items-center gap-2 text-token-lg font-semibold capitalize"
            >
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{ backgroundColor: activeSources[0].color }}
              />
              {activeSources[0].name}
            </span>
            <p className="text-token-md text-muted-foreground">
              All revenue from a single source: {gbpWhole(activeSources[0].value)}
              {Number.isFinite(activeSources[0].count) && ` across ${activeSources[0].count.toLocaleString()} orders`}
            </p>
          </div>
        ) : (
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={bySource}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="35%"
                  outerRadius="70%"
                  label={renderCustomLabel}
                  labelLine={false}
                  isAnimationActive={false}
                >
                  {bySource.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => gbpWhole(value)} />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                  iconSize={12}
                  formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      {/* By Type — summary table (denser and more precise than the old donut
          for a single power user; same client-side derivation from timeSeries) */}
      <ChartCard title="Revenue by Order Type">
        {byType.length === 0 ? (
          <EmptyMessage>No type data</EmptyMessage>
        ) : (
          <table className="w-full text-token-md">
            <thead>
              <tr className="border-b border-borderGrey text-token-sm text-muted-foreground">
                <th className="py-2 pr-3 text-left font-medium">Type</th>
                <th className="py-2 px-3 text-right font-medium">Revenue</th>
                <th className="py-2 pl-3 text-right font-medium">Share</th>
              </tr>
            </thead>
            <tbody>
              {byType.map((item) => (
                <tr key={item.category} className="border-b border-borderGrey last:border-0">
                  <td className="py-2.5 pr-3">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      {item.name}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-right font-medium tabular-nums">
                    {gbpWhole(item.value)}
                  </td>
                  <td className="py-2.5 pl-3 text-right tabular-nums text-muted-foreground">
                    {formatPercent(item.percentage)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-borderGrey">
                <td className="py-2.5 pr-3 font-semibold">Total</td>
                <td className="py-2.5 px-3 text-right font-semibold tabular-nums">
                  {gbpWhole(typeTotal)}
                </td>
                <td className="py-2.5 pl-3 text-right tabular-nums text-muted-foreground">100%</td>
              </tr>
            </tfoot>
          </table>
        )}
      </ChartCard>
    </div>
  );
}
